import { parseFoma } from "../fsm/parseFoma";
import { convertPhonotactics } from "../phonotactics/phonotactics";
import { compileStressRule } from "../stress/stressRule";
import { compileLengthRules } from "./compileLengthRules";
import { CompiledRule, compileRule, isRuleError } from "./compileRule";
import { SegmentStrategy } from "./intoSegments";
import { rulesToLookup } from "./replaceLexicalSets";
import { Rule } from "./rule";
import { CompiledRuleSet, getJustRules, Rules2Type, RuleSet, RuleType } from "./ruleSet";
import { FailedRule } from "./rulesRunner";

export function compileRuleSet(ruleSet: RuleSet): [CompiledRuleSet, FailedRule[]] {
  const errors: FailedRule[] = [];
  const justRules = getJustRules(ruleSet.rules);
  const rules: CompiledRule[] = compileSingleRules(errors, justRules, ruleSet.inputSegments, ruleSet.outputSegments, ruleSet.sortRulesByInputLength);
  const in2 = ruleSet.inputSegments2 ?? ruleSet.inputSegments;
  const out2 = ruleSet.outputSegments2 ?? ruleSet.outputSegments;
  const rules2 = compileMultipleRuleLists(errors, ruleSet.rules2, in2, out2, ruleSet.sortRulesByInputLength);
  const fsm = ruleSet.fsm ? parseFoma(ruleSet.fsm) : undefined;
  const stressRule = ruleSet.stressRule ? compileStressRule(ruleSet.stressRule) : undefined;
  const syllableBreaks = ruleSet.syllableBreaks === undefined ? undefined
    : Array.isArray(ruleSet.syllableBreaks) ? ruleSet.syllableBreaks
      : convertPhonotactics(ruleSet.syllableBreaks).phonotactics;
  const phonotactics = ruleSet.phonotactics === undefined ? []  // empty
    : Array.isArray(ruleSet.phonotactics) ? ruleSet.phonotactics // diphthongs
      : convertPhonotactics(ruleSet.phonotactics).phonotactics;  // full phonotactics

  const addPhonemeLength = compileLengthRules(ruleSet.phonemeLengths);

  const compiled: CompiledRuleSet = {
    preprocess: ruleSet.preprocess,
    wholeWords: ruleSet.wholeWords,
    useRPDictionary: ruleSet.useRPDictionary,
    lexicalSetLookup: ruleSet.lexicalSets ? rulesToLookup(ruleSet.lexicalSets) : [],
    syllableBreaks,
    inputSegments: ruleSet.inputSegments ?? SegmentStrategy.Ipa,
    outputSegments: ruleSet.outputSegments ?? SegmentStrategy.Ipa,
    subsetMatches: ruleSet.subsetMatches,
    rules,
    diacriticRules: ruleSet.diacriticRules,
    rules2: rules2,
    addPhonemeLength,
    fsm,
    stressRule: Array.isArray(stressRule) ? undefined : stressRule,
    phonotactics
  };
  return [compiled, errors];
}


function compileMultipleRuleLists(
  errors: FailedRule[],
  ruleSets?: Rules2Type,
  inputSegments?: SegmentStrategy,
  outputSegments?: SegmentStrategy,
  sortRulesByInputLength?: boolean,
): CompiledRule[][] | undefined {
  if (ruleSets === undefined || ruleSets.length === 0) {
    return undefined;
  }

  // single set of rules
  if (!Array.isArray(ruleSets[0])) {
    const justRules2 = getJustRules(ruleSets as RuleType[]);
    return [compileSingleRules(errors, justRules2, inputSegments, outputSegments, sortRulesByInputLength)];
  }

  // multiple sets of rules
  ruleSets = ruleSets as RuleType[][];
  const compiled: CompiledRule[][] = [];
  for (const rs of ruleSets) {
    const justRules2 = getJustRules(rs);
    if (justRules2 && justRules2.length > 0) {
      compiled.push(compileSingleRules(errors, justRules2, inputSegments, outputSegments, sortRulesByInputLength));
    }
  }
  return compiled;
}

function compileSingleRules(
  errors: FailedRule[],
  rules?: Rule[],
  inputSegments?: SegmentStrategy,
  outputSegments?: SegmentStrategy,
  sortRulesByInputLength?: boolean,
): CompiledRule[] {
  if (!rules) {
    return [];
  }

  const compiled: CompiledRule[] = [];
  for (const rule of rules) {
    const result = compileRule(rule, inputSegments, outputSegments);
    if (isRuleError(result)) {
      errors.push({ rule, error: result });
    } else {
      compiled.push(result);
    }
  }

  if (sortRulesByInputLength) {
    // rules with the longest 'from' should come first so the shorter rules don't stomp on them
    return compiled.sort((r1, r2) => {
      // first compare segment length (e.g. ['dʒ', 'i']),
      // if same, then compare number of symbols in first segment (e.g., /i̯/ vs. /i/)
      const segmentDiff = r2.from.length - r1.from.length;
      return segmentDiff !== 0 ? segmentDiff
        : r2.from[0].length - r1.from[0].length;
    });
  }
  return compiled;
}
